import React, { useState, useEffect, useCallback } from 'react';
import ChartRenderer, { ChartConfig } from './ChartRenderer';
import { queryBackendDuckDB } from '../services/backendDuckDB';

interface FilterableChartProps {
  config: ChartConfig;
  data: Record<string, unknown>[];
  tableName?: string;
  baseSql?: string;
}

const FilterableChart: React.FC<FilterableChartProps> = ({ config, data, tableName, baseSql: baseSqlProp }) => {
  // Use baseSql from prop, fall back to config.source_sql
  const baseSql = baseSqlProp || config.source_sql;
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({});
  const [filterSelections, setFilterSelections] = useState<Record<string, string>>({});
  const [filteredData, setFilteredData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  // Use source_table for filter queries (raw data), fall back to tableName
  const filterTable = config.source_table || tableName;

  // Check if we have filters and the infrastructure to support them
  const hasFilters = config.filters && config.filters.length > 0 && filterTable;

  // Load distinct values for each filter column from backend DuckDB
  useEffect(() => {
    if (!hasFilters || !filterTable) return;

    const loadOptions = async () => {
      const options: Record<string, string[]> = {};
      for (const filter of config.filters!) {
        try {
          const result = await queryBackendDuckDB(
            `SELECT DISTINCT "${filter.column}" FROM "${filterTable}" WHERE "${filter.column}" IS NOT NULL ORDER BY "${filter.column}"`
          );
          if (result.success && result.rows) {
            options[filter.column] = result.rows.map((r: any) => String(r[filter.column]));
          }
        } catch (e) {
          console.error(`Failed to load filter options for ${filter.column}:`, e);
        }
      }
      setFilterOptions(options);

      // Initialize all filters to "All"
      const initial: Record<string, string> = {};
      for (const filter of config.filters!) {
        initial[filter.column] = 'All';
      }
      setFilterSelections(initial);
    };
    loadOptions();
  }, [hasFilters, filterTable, config.filters]);

  // Load initial data from backend DuckDB if not provided inline
  useEffect(() => {
    if ((!data || data.length === 0) && baseSql && tableName) {
      const loadInitial = async () => {
        const result = await queryBackendDuckDB(baseSql);
        if (result.success) {
          setFilteredData(result.rows);
        }
      };
      loadInitial();
    }
  }, [data, baseSql, tableName]);

  // Re-query when filter changes
  useEffect(() => {
    console.log('[FilterableChart] Filter effect triggered:', {
      hasFilters, baseSql: baseSql?.substring(0, 80), filterTable,
      filterSelections, dataLen: data?.length
    });
    if (!hasFilters || !baseSql || !filterTable) {
      console.log('[FilterableChart] Short-circuit: hasFilters=', hasFilters, 'baseSql=', !!baseSql, 'filterTable=', filterTable);
      setFilteredData(data);
      return;
    }

    const activeFilters = Object.entries(filterSelections)
      .filter(([_, v]) => v && v !== 'All');

    const requery = async () => {
      setIsLoading(true);
      try {
        let querySql: string;
        if (activeFilters.length === 0) {
          // "All" selected — re-query without WHERE (consistent data source)
          querySql = baseSql!;
        } else {
          const whereClause = activeFilters
            .map(([col, val]) => `"${col}" = '${val.replace(/'/g, "''")}'`)
            .join(' AND ');
          querySql = injectWhereClause(baseSql!, whereClause);
        }
        console.log('[FilterableChart] Re-query SQL:', querySql);

        const result = await queryBackendDuckDB(querySql);
        console.log('[FilterableChart] Re-query result:', result.success, result.rowCount, 'rows', result.error);
        if (result.success && result.rows) {
          setFilteredData(result.rows);
        } else {
          console.error('[FilterableChart] Re-query failed:', result.error);
        }
      } catch (e) {
        console.error('[FilterableChart] Filter query exception:', e);
        setFilteredData(data);
      }
      setIsLoading(false);
    };
    requery();
  }, [filterSelections, baseSql, tableName, data, hasFilters]);

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterSelections(prev => ({ ...prev, [column]: value }));
  }, []);

  // If no filters configured, render chart directly
  if (!hasFilters) {
    return <ChartRenderer config={config} data={data} />;
  }

  return (
    <div className="filterable-chart">
      <div className="chart-filters">
        {config.filters!.map(filter => (
          <div key={filter.column} className="chart-filter">
            <span className="chart-filter-label">{filter.label}:</span>
            <select
              value={filterSelections[filter.column] || 'All'}
              onChange={e => handleFilterChange(filter.column, e.target.value)}
            >
              <option value="All">All</option>
              {(filterOptions[filter.column] || []).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        ))}
        {isLoading && <span className="chart-filter-loading">Updating...</span>}
      </div>
      <ChartRenderer config={config} data={filteredData} />
    </div>
  );
};

/** Inject a WHERE clause into a SQL query (handles UNION ALL) */
function injectWhereClause(sql: string, whereClause: string): string {
  // Split on UNION ALL to handle each subquery independently
  const unionParts = sql.split(/\bUNION\s+ALL\b/i);
  if (unionParts.length > 1) {
    return unionParts.map(part => injectWhereIntoSingle(part.trim(), whereClause)).join(' UNION ALL ');
  }
  return injectWhereIntoSingle(sql, whereClause);
}

/** Inject a WHERE clause into a single (non-UNION) SQL query */
function injectWhereIntoSingle(sql: string, whereClause: string): string {
  const upperSql = sql.toUpperCase();
  const hasWhere = upperSql.includes('WHERE');

  // Find insertion point: before GROUP BY, ORDER BY, LIMIT, HAVING
  const markers = ['GROUP BY', 'ORDER BY', 'LIMIT', 'HAVING'];
  let insertPos = sql.length;
  for (const marker of markers) {
    const pos = upperSql.indexOf(marker);
    if (pos > 0 && pos < insertPos) insertPos = pos;
  }

  if (hasWhere) {
    return sql.slice(0, insertPos) + ` AND ${whereClause} ` + sql.slice(insertPos);
  } else {
    return sql.slice(0, insertPos) + ` WHERE ${whereClause} ` + sql.slice(insertPos);
  }
}

export default FilterableChart;
